import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Maximize2 } from 'lucide-react';
import ChartExpandModal from './ChartExpandModal';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format } from 'date-fns';

const EXCLUDED_INCOME_CATEGORIES = new Set([
  'Transfer - ANZ Offset',
]);

const EXCLUDED_CATEGORIES = new Set([
  'Credit Card Repayments',
  'Transfer - Investments',
  'Transfer - School Fees',
  'Transfer - Sue Martin',
  'Transfer - Robinsons',
  'Transfer - Holidays',
  'Transfer - Cameron Savings',
  'Transfer - Utilities',
  'Transfer - Deferred Wages',
  'Transfer - Rates',
  'Transfer - Rego',
  'Transfer - No Touchy',
  'Transfer - Alice Savings',
]);

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm text-sm">
      <p className="font-medium text-slate-700">{payload[0].payload.month}</p>
      <p className="text-emerald-600">${payload[0].value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
    </div>
  );
};

const YOYTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm text-sm space-y-1">
      <p className="font-medium text-slate-700">{label}</p>
      {payload.map((p, i) => p.value != null && (
        <p key={i} style={{ color: p.color }}>{p.name}: ${p.value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
      ))}
    </div>
  );
};

export default function MonthlyIncomeTrendChart({ transactions, allTransactions = [], filters }) {
  const [expanded, setExpanded] = useState(false);
  const isMonthView = filters?.periodType === 'month';

  const yoyData = useMemo(() => {
    if (!isMonthView) return null;
    const { month, year, categories } = filters;
    const years = [year - 2, year - 1, year];
    const monthName = format(new Date(year, month - 1, 1), 'MMMM');

    return years.map(y => {
      let txs = allTransactions.filter(t => t.type === 'credit' && t.month === month && t.year === y && !EXCLUDED_CATEGORIES.has(t.category) && !EXCLUDED_INCOME_CATEGORIES.has(t.category));
      if (categories?.length > 0) txs = txs.filter(t => categories.includes(t.category));
      const total = txs.reduce((sum, t) => sum + (t.amount || 0), 0);
      return { year: String(y), amount: total > 0 ? parseFloat(total.toFixed(2)) : null };
    });
  }, [isMonthView, filters, allTransactions]);

  const data = useMemo(() => {
    if (isMonthView) return [];
    const grouped = {};
    transactions
      .filter(t => t.type === 'credit' && t.year && t.month)
      .forEach(t => {
        const key = `${t.year}-${String(t.month).padStart(2, '0')}`;
        grouped[key] = (grouped[key] || 0) + (t.amount || 0);
      });
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, amount]) => {
        const [y, m] = key.split('-').map(Number);
        return {
          month: format(new Date(y, m - 1, 1), 'MMM yy'),
          amount: parseFloat(amount.toFixed(2)),
        };
      });
  }, [transactions, isMonthView]);

  const monthLabel = isMonthView ? format(new Date(filters.year, filters.month - 1, 1), 'MMMM') : '';

  return (
    <div>
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {isMonthView ? `${monthLabel} Income – Year on Year` : 'Monthly Income Trend'}
          </CardTitle>
          {!isMonthView && (
            <button onClick={() => setExpanded(true)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
        </CardHeader>
        <CardContent>
          {isMonthView ? (
            yoyData && yoyData.some(d => d.amount != null) ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={yoyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="year" fontSize={11} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} fontSize={11} axisLine={false} tickLine={false} />
                  <Tooltip content={<YOYTooltip />} />
                  <Line type="monotone" dataKey="amount" name={monthLabel} stroke="#10b981" strokeWidth={2.5} dot={{ r: 5, fill: '#10b981' }} activeDot={{ r: 7 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-56 text-slate-400 text-sm">No data for this period</div>
            )
          ) : (
            <>
              {data.length === 0 ? (
                <div className="flex items-center justify-center h-56 text-slate-400 text-sm">No data for this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" fontSize={11} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} fontSize={11} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="amount" stroke="#10b981" fill="url(#incomeGradient)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {!isMonthView && (
        <ChartExpandModal
          open={expanded}
          onClose={() => setExpanded(false)}
          title="Monthly Income Trend"
          data={data}
          chartType="area"
          color="#10b981"
        />
      )}
    </div>
  );
}